import { useEffect } from 'react';
import type { ReactElement } from 'react';
import type { ReactNode } from 'react';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  overlayClassName?: string;
  panelClassName?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
};

export function Modal({
  isOpen,
  onClose,
  children,
  ariaLabel,
  overlayClassName = 'fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4',
  panelClassName = 'w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-xl',
  closeOnOverlayClick = true,
  closeOnEscape = true,
}: ModalProps): ReactElement | null {
  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeOnEscape, isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={overlayClassName}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={() => {
        if (closeOnOverlayClick) {
          onClose();
        }
      }}
    >
      <div
        className={panelClassName}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
}
